/**
 * Box totals: what a file adds up to, box by box, before anything is filed.
 *
 * The Tab Service portal shows these after a file's columns are mapped and validated, and
 * customers read them the way they read a bank statement - the number they expect to see is
 * the number in their own ledger, so a column mapped to the wrong box or a file exported
 * with the wrong rows shows up here as a total that is off, before the IRS ever sees it.
 *
 * Which columns are boxes comes from core-api's own per-year field definitions, plus the
 * hand-written rules for e-file-app-only forms (fieldRules.ts), so this cannot call a country
 * an amount. Money columns are summed. Checkbox columns that are boxes on the form (BOX...) are counted, the way the
 * portal counts 5498-SA box 6 coverage types.
 */
import { fieldRules } from "./fieldRules";
import { canonicalCheckbox } from "./schemas";

export interface BoxTotal {
  /** The canonical column, e.g. BOX1A_NONEMPLOYEE_COMPENSATION. */
  field: string;
  /** core-api's label for it, e.g. "Nonemployee Compensation". */
  label: string;
  /** Money added up, or ticked boxes counted. */
  kind: "amount" | "count";
  /** The sum (two decimals) or the count. */
  total: number;
  /** How many records had a value in this box. */
  records: number;
}

/** The number a money cell holds: "$1,234.50" and 1234.5 are the same amount. Blank is none. */
function amountOf(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = String(value).trim().replace(/[$,]/g, "");
  if (text === "") return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/** A column that is a box on the form: BOX1A_..., BOX_2ND_..., OTHER_LINE_... */
const BOX_FIELD = /^(BOX|OTHER_LINE)/i;

/**
 * Boxes that changed name between tax years, so a record written under one year's name is
 * read under the other's. The Pub 1220 intake and the JSON path both produce the newest
 * name (1099-NEC box 1 became 1a in 2026; the generator reads BOX1A and falls back to BOX1),
 * while a 2025 e-file's rules still call the box BOX1. Without this a 55,000-record 2025
 * 1220 file totalled $0.00 of nonemployee compensation, which was the file being misread,
 * not the file being empty.
 */
const RENAMED_BOXES: Record<string, string[][]> = {
  "1099-NEC": [
    ["BOX1_NONEMPLOYEE_COMPENSATION", "BOX1A_NONEMPLOYEE_COMPENSATION"],
    ["BOX2_DIRECT_SALE", "BOX2_DIRECT_SALES"],
  ],
};

const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === "string" && value.trim() === "");

/** The record's value for a box, under this year's name or any other year's name for it. */
function readBox(record: Record<string, unknown>, formType: string, field: string): unknown {
  const own = record?.[field];
  if (!isBlank(own)) return own;
  for (const group of RENAMED_BOXES[formType] ?? []) {
    if (!group.includes(field)) continue;
    for (const alias of group) {
      if (alias === field) continue;
      const value = record?.[alias];
      if (!isBlank(value)) return value;
    }
  }
  return own;
}

/** Rounded to cents the way the portal's totals are, so 0.1 + 0.2 reads as 0.30. */
const cents = (value: number): number => Math.round(value * 100) / 100;

/**
 * Every box on the form, in core-api's order, with what the records add up to.
 *
 * Boxes nobody filled are included at zero: a customer expecting box 4 withholding to be
 * present wants to see 0.00 there, not have it missing from the list.
 */
export function boxTotals(
  formType: string,
  taxYear: number,
  records: Record<string, unknown>[]
): BoxTotal[] {
  const rules = fieldRules(formType, taxYear);
  const out: BoxTotal[] = [];
  for (const [field, rule] of Object.entries(rules)) {
    // Only the boxes printed on the form, the same key the portal's box-total report uses.
    // core-api types 1099_YEAR and SITE_ID as numbers too, and neither is a total.
    if (!BOX_FIELD.test(field)) continue;
    const isAmount = rule.kind === "amount";
    const isCountedBox = rule.kind === "checkbox";
    if (!isAmount && !isCountedBox) continue;

    let total = 0;
    let filled = 0;
    for (const record of records) {
      const value = readBox(record, formType, field);
      if (isAmount) {
        const amount = amountOf(value);
        if (amount === null) continue;
        total += amount;
        filled += 1;
      } else if (canonicalCheckbox(value) === "X") {
        total += 1;
        filled += 1;
      }
    }
    out.push({
      field,
      label: rule.label ?? field,
      kind: isAmount ? "amount" : "count",
      total: isAmount ? cents(total) : total,
      records: filled,
    });
  }
  for (const extra of unruledBoxes(rules, records)) out.push(extra);
  return out;
}

/** BOX14_GOLDEN_PARACHUTE -> "Box 14 Golden Parachute", when core-api has no label for it. */
function labelFor(field: string): string {
  return field
    .replace(/^BOX_?/, "BOX_")
    .split("_")
    .filter((word) => word !== "")
    .map((word) =>
      /^\d/.test(word) ? word : word.charAt(0) + word.slice(1).toLowerCase()
    )
    .map((word) => (word === "BOX" ? "Box" : word))
    .join(" ");
}

/**
 * Boxes the file carries that this year's rules do not define.
 *
 * core-api's per-year definitions are the authority on what a column is, but they have
 * gaps: 1099-MISC box 14, excess golden parachute payments, is a column the MISC record
 * carries and no 2025 rule describes, so a file with money in it showed nothing at all on
 * Box totals (TS-8291). A box the file filled is reported rather than silently dropped.
 *
 * Only what the records actually put a value in, and only when that value reads as money or
 * as a ticked box - a free-text box stays out, and a box no record filled is not invented.
 */
function unruledBoxes(
  rules: Record<string, { kind?: string }>,
  records: Record<string, unknown>[]
): BoxTotal[] {
  const seen = new Set<string>();
  for (const record of records) {
    for (const field of Object.keys(record ?? {})) {
      if (rules[field] || seen.has(field) || !BOX_FIELD.test(field)) continue;
      seen.add(field);
    }
  }
  const out: BoxTotal[] = [];
  for (const field of seen) {
    let total = 0;
    let filled = 0;
    let ticks = 0;
    let amounts = 0;
    for (const record of records) {
      const value = record?.[field];
      if (isBlank(value)) continue;
      filled += 1;
      const amount = amountOf(value);
      if (amount !== null) {
        amounts += 1;
        total += amount;
      } else if (canonicalCheckbox(value) === "X") {
        ticks += 1;
      }
    }
    if (filled === 0) continue;
    if (amounts > 0) {
      out.push({ field, label: labelFor(field), kind: "amount", total: cents(total), records: amounts });
    } else if (ticks > 0) {
      out.push({ field, label: labelFor(field), kind: "count", total: ticks, records: ticks });
    }
  }
  return out;
}
